import React from 'react';
import { Button, Drawer, message, Skeleton, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import TablePaginationBar from '../../../../../components/TablePaginationBar';
import AsyncReportDownloadModal, {
    dashboardAsyncReportLabelI18nKeys,
} from '../../../../../components/asyncReportDownload';
import CentralParcelDetailDrawer from '../../../../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo } from '../../../../../utils/auth';
import {
    GET_INHUB_SORTING_DETAIL_PAGE,
    type GetInhubSortingDetailPageResponse,
    type InhubSortingDetailRowResp,
    type InhubSortingMetric
} from '../graphql/queries/getInhubSortingDetailPage';
import {
    GENERATE_INHUB_SORTING_DETAIL_EXPORT,
    type GenerateInhubSortingDetailExportResponse,
    type GenerateInhubSortingDetailExportVariables
} from '../graphql/mutations/generateInhubSortingDetailExport';
import type { InhubSortingDimension } from '../graphql/queries/getInhubSortingAccountSummariesPage';

const I18N_BASE = 'wms.delivery.inhubDashboard.sorting';
const DEFAULT_PAGE_SIZE = 10;

interface Props {
    open: boolean;
    onClose: () => void;
    hubId: number;
    rangeStart: string;
    rangeEnd: string;
    dimension: InhubSortingDimension;
    groupKey: number | null;
    // ACCOUNT 维度下钻时该行的 accountName；TEAM 维度传 null。
    // 数仓 accountId → accountName 是多对多，不带这个字段会把同 id 下其它 name 的明细一起拉出来。
    accountName: string | null;
    // TEAM 维度点击具体队伍时该行的 laborTeamName；ACCOUNT 维度或点击"未分组"桶传 null。
    // 汇总层同名多 laborTeamId 会合并成一行 (laborTeamId=null)，只按 groupKey 拉不到全部数据；
    // 后端拿这个 name 反向解析出所有映射到它的 labor_team_id 集合走严格 inList。
    laborTeamName: string | null;
    metric: InhubSortingMetric;
    title?: string;
    // KPI 卡数字下钻传 true：忽略 groupKey/未分组桶过滤，拉该指标下 hub+时间窗内的全量明细
    matchAllGroups?: boolean;
}

const SortingDetailDrawer: React.FC<Props> = ({
    open,
    onClose,
    hubId,
    rangeStart,
    rangeEnd,
    dimension,
    groupKey,
    accountName,
    laborTeamName,
    metric,
    title,
    matchAllGroups
}) => {
    const { t } = useTranslation();
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [parcelDetailVisible, setParcelDetailVisible] = React.useState(false);
    const [selectedOrderId, setSelectedOrderId] = React.useState<string>('');

    React.useEffect(() => {
        if (open) {
            setPage(1);
        }
    }, [open, hubId, rangeStart, rangeEnd, dimension, groupKey, accountName, laborTeamName, metric, matchAllGroups]);

    const { data, loading } = useQuery<GetInhubSortingDetailPageResponse>(
        GET_INHUB_SORTING_DETAIL_PAGE,
        {
            variables: {
                filter: {
                    hubId,
                    rangeStart,
                    rangeEnd,
                    dimension,
                    groupKey,
                    accountName,
                    laborTeamName,
                    metric,
                    page,
                    pageSize,
                    matchAllGroups: matchAllGroups ?? false
                }
            },
            skip: !open,
            errorPolicy: 'all',
            notifyOnNetworkStatusChange: true
        }
    );

    const rows = data?.getInhubSortingDetailPage?.content ?? [];
    const total = data?.getInhubSortingDetailPage?.totalElements ?? 0;

    const [exportMutation, { loading: exportLoading }] = useMutation<
        GenerateInhubSortingDetailExportResponse,
        GenerateInhubSortingDetailExportVariables
    >(GENERATE_INHUB_SORTING_DETAIL_EXPORT);
    const [downloadModalVisible, setDownloadModalVisible] = React.useState(false);
    const [generatedFileName, setGeneratedFileName] = React.useState('');

    const handleExport = async () => {
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const result = await exportMutation({
                variables: {
                    filter: {
                        hubId,
                        rangeStart,
                        rangeEnd,
                        dimension,
                        groupKey,
                        accountName,
                        laborTeamName,
                        metric,
                        page: 1,
                        pageSize: 1,
                        matchAllGroups: matchAllGroups ?? false
                    },
                    timezone: tz
                }
            });
            const fileName = result.data?.generateInhubSortingDetailExport;
            if (fileName) {
                setGeneratedFileName(fileName);
                setDownloadModalVisible(true);
            }
        } catch (e: unknown) {
            const errMsg = e instanceof Error ? e.message : String(e);
            message.error(errMsg);
        }
    };

    const columns: ColumnsType<InhubSortingDetailRowResp> = [
        {
            title: t(`${I18N_BASE}.detail.columns.trackingNumber`),
            dataIndex: 'trackingNumber',
            width: 180,
            render: (text: string, record: InhubSortingDetailRowResp) => (
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setSelectedOrderId(String(record.orderId));
                        setParcelDetailVisible(true);
                    }}
                    style={{ color: '#1890ff' }}
                >
                    {text ?? '-'}
                </a>
            )
        },
        {
            title: t(`${I18N_BASE}.detail.columns.overallStatus`),
            dataIndex: 'overallStatus',
            width: 140,
            render: (s: string) => (s ? t(`enum.overallStatus.${s}`, { defaultValue: s }) : '-')
        },
        {
            title: t(`${I18N_BASE}.detail.columns.bagNumber`),
            dataIndex: 'bagNumber',
            width: 190
        },
        {
            title: t(`${I18N_BASE}.detail.columns.sortingCode`),
            dataIndex: 'sortingCode',
            width: 140
        },
        {
            title: t(`${I18N_BASE}.detail.columns.accountName`),
            dataIndex: 'accountName',
            width: 140
        },
        {
            title: t(`${I18N_BASE}.detail.columns.laborTeamName`),
            dataIndex: 'laborTeamName',
            width: 140
        }
    ];

    return (
        <Drawer
            title={title ?? t(`${I18N_BASE}.detail.title`)}
            placement="right"
            width={960}
            open={open}
            onClose={onClose}
            destroyOnClose
            closable={false}
            extra={<CloseOutlined onClick={onClose} className="cursor-pointer" />}
        >
            <div className="flex justify-end mb-3">
                <Button
                    type="primary"
                    loading={exportLoading}
                    onClick={handleExport}
                    disabled={total === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t(`${I18N_BASE}.detail.export.button`)}</span>
                </Button>
            </div>
            {loading && rows.length === 0 ? (
                <Skeleton active paragraph={{ rows: 6 }} />
            ) : (
                <Table<InhubSortingDetailRowResp>
                    rowKey={(r, idx) => `${r.trackingNumber}-${idx}`}
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    size="middle"
                    scroll={{ x: 900 }}
                    loading={loading}
                />
            )}
            <TablePaginationBar
                className="mt-4"
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={(p, ps) => {
                    setPage(p);
                    setPageSize(ps);
                }}
                onShowSizeChange={(_, ps) => {
                    setPage(1);
                    setPageSize(ps);
                }}
            />

            <CentralParcelDetailDrawer
                visible={parcelDetailVisible}
                orderId={selectedOrderId}
                userRole={getUserInfo()?.account?.role?.name ?? ''}
                onClose={() => setParcelDetailVisible(false)}
            />
            <AsyncReportDownloadModal
                visible={downloadModalVisible}
                onClose={() => setDownloadModalVisible(false)}
                fileName={generatedFileName}
                reportLabelI18nKey={dashboardAsyncReportLabelI18nKeys.inhubSorting}
            />
        </Drawer>
    );
};

export default SortingDetailDrawer;
